import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Input,
    Row,
    Spinner,
    UncontrolledAlert,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { DATABASE_VEHICULE_EDIT, DATABASE_VEHICULE_FORM } from "../../routes/routeConstants";
import { useDispatch, useSelector } from "react-redux";
import { vehiculeActions } from "./vehiculeSlice";
import { isVehiclePhoto, resolveDocumentUrl } from "./documentUrl";
import { EXPERT_ROLES, getCurrentRoles, hasAnyRole } from "../../helpers/roles";

const getVehicleTitle = (vehicle) => {
    const title = [vehicle?.marque, vehicle?.modele].filter(Boolean).join(" ");
    return title || vehicle?.numChassis || vehicle?.immatriculation || "Vehicule";
};

const formatAmount = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const amount = Number(value);
    if (Number.isNaN(amount)) return value;
    return new Intl.NumberFormat("fr-MA", { maximumFractionDigits: 2 }).format(amount);
};

function VehiculePage() {
    const { t } = useTranslation("translation");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { vehicules = [], isFetching, total = 0 } = useSelector(state => state.vehicule);
    const canCreateVehicule = hasAnyRole(getCurrentRoles(), EXPERT_ROLES);
    const [record, setRecord] = useState({ open: false, data: null });
    const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "" });
    const [pagination, setPagination] = useState({ page: 0, size: 25 });
    const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);

    const load = useCallback((page = pagination.page, size = pagination.size) => {
        dispatch(vehiculeActions.search({
            criteria: {},
            page,
            size,
            sortField: "createdDate",
            direction: "DESC",
        }));
    }, [dispatch, pagination.page, pagination.size]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (location.state?.successMessage) {
            setSuccessMessage(location.state.successMessage);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    const totalItems = total || vehicules.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.size));
    const currentPage = Math.min(pagination.page + 1, totalPages);
    const startItem = totalItems === 0 ? 0 : pagination.page * pagination.size + 1;
    const endItem = Math.min((pagination.page + 1) * pagination.size, totalItems);

    useEffect(() => {
        if (totalItems > 0 && pagination.page >= totalPages) {
            setPagination(prev => ({ ...prev, page: totalPages - 1 }));
        }
    }, [pagination.page, totalItems, totalPages]);

    const goToPage = (page) => {
        const nextPage = Math.min(Math.max(page, 0), totalPages - 1);
        setPagination(prev => ({ ...prev, page: nextPage }));
    };

    const changePageSize = (event) => {
        setPagination({ page: 0, size: Number(event.target.value) });
    };

    const renderPagination = () => totalItems > 0 && (
        <Card className="border-0 shadow-sm mb-3">
            <CardBody className="py-2">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="text-muted small">
                        Affichage {startItem}-{endItem} sur {totalItems} vehicules
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="text-muted small">Par page</span>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={pagination.size}
                            onChange={changePageSize}
                            style={{ width: 86 }}
                        >
                            {[10, 25, 50, 100].map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </Input>
                        <Button size="sm" color="light" disabled={pagination.page === 0} onClick={() => goToPage(0)}>
                            <i className="ri-skip-left-line"></i>
                        </Button>
                        <Button size="sm" color="light" disabled={pagination.page === 0} onClick={() => goToPage(pagination.page - 1)}>
                            <i className="ri-arrow-left-s-line"></i>
                        </Button>
                        <span className="text-muted small">Page {currentPage} / {totalPages}</span>
                        <Button size="sm" color="light" disabled={pagination.page >= totalPages - 1} onClick={() => goToPage(pagination.page + 1)}>
                            <i className="ri-arrow-right-s-line"></i>
                        </Button>
                        <Button size="sm" color="light" disabled={pagination.page >= totalPages - 1} onClick={() => goToPage(totalPages - 1)}>
                            <i className="ri-skip-right-line"></i>
                        </Button>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    const handleDelete = (vehicle) => {
        setRecord({ open: true, data: vehicle });
    };

    const confirmDelete = () => {
        setRecord({ ...record, open: false });
        dispatch(vehiculeActions.delete({
            id: record.data.id,
            onSuccess: load,
            onError: (error) => console.log(error),
        }));
    };

    const vehiclePhotos = (vehicle) => (vehicle?.documents || []).filter(isVehiclePhoto);
    const openPhotoViewer = (vehicle, index = 0) => {
        const photos = vehiclePhotos(vehicle);
        if (photos.length === 0) return;
        setPhotoViewer({ open: true, photos, index, title: getVehicleTitle(vehicle) });
    };
    const closePhotoViewer = () => setPhotoViewer(prev => ({ ...prev, open: false }));

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card className="page-compact-header mb-3 border-0 shadow-sm">
                        <CardBody>
                            <Row className="align-items-center g-3">
                                <Col lg={5}>
                                    <div className="page-inline-breadcrumb mb-2">
                                        <span>{t("database")}</span>
                                        <i className="ri-arrow-right-s-line"></i>
                                        <strong>{t("vehicule")}</strong>
                                    </div>
                                    <h4 className="mb-1">{t("vehicule")}</h4>
                                    <p className="text-muted mb-0">Gestion rapide du parc vehicule.</p>
                                </Col>
                                <Col lg={7}>
                                    <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                                        {canCreateVehicule && (
                                            <Button color="primary" onClick={() => navigate(DATABASE_VEHICULE_FORM)}>
                                                <i className="ri-add-line me-1"></i>
                                                Nouveau vehicule
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {successMessage && (
                        <UncontrolledAlert color="success" className="shadow-sm border-0">
                            <i className="ri-checkbox-circle-line me-1"></i>
                            {successMessage}
                        </UncontrolledAlert>
                    )}

                    {isFetching ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                        </div>
                    ) : vehicules.length === 0 ? (
                        <Card className="border-0 shadow-sm">
                            <CardBody className="text-center py-5">
                                <i className="ri-car-line display-5 text-muted"></i>
                                <h5 className="mt-3">Aucun vehicule trouve</h5>
                                <p className="text-muted mb-3">
                                    {canCreateVehicule ? "Ajoutez un nouveau vehicule pour demarrer le parc." : "Aucun vehicule n'est disponible pour le moment."}
                                </p>
                                {canCreateVehicule && (
                                    <Button color="primary" onClick={() => navigate(DATABASE_VEHICULE_FORM)}>Nouveau vehicule</Button>
                                )}
                            </CardBody>
                        </Card>
                    ) : (
                        <>
                            {renderPagination()}
                            <div className="vehicle-card-grid">
                                {vehicules.map((vehicle) => {
                                    const photos = vehiclePhotos(vehicle);
                                    const coverPhoto = photos[0];

                                    return (
                                        <Card className="h-100 border-0 shadow-sm vehicle-card" key={vehicle.id}>
                                                <button
                                                    type="button"
                                                    className={`vehicle-card-photo ${coverPhoto ? "" : "is-empty"}`}
                                                    style={coverPhoto ? { backgroundImage: `url("${resolveDocumentUrl(coverPhoto)}")` } : undefined}
                                                    onClick={() => openPhotoViewer(vehicle)}
                                                    disabled={!coverPhoto}
                                                    aria-label={coverPhoto ? `Voir les photos de ${getVehicleTitle(vehicle)}` : "Aucune photo disponible"}
                                                >
                                                    {coverPhoto ? (
                                                        <span className="vehicle-photo-count">
                                                            <i className="ri-image-line me-1"></i>
                                                            {photos.length}
                                                        </span>
                                                    ) : (
                                                        <span className="vehicle-photo-empty">
                                                            <i className="ri-image-add-line"></i>
                                                            Aucune photo
                                                        </span>
                                                    )}
                                                </button>
                                                <CardBody className="vehicle-card-body">
                                                <div className="d-flex justify-content-between gap-2 mb-2">
                                                    <div>
                                                        <h6 className="mb-1 text-truncate" title={getVehicleTitle(vehicle)}>{getVehicleTitle(vehicle)}</h6>
                                                        <div className="text-muted small">{vehicle.numChassis || "Chassis non renseigne"}</div>
                                                    </div>
                                                    <Badge color={vehicle.reform === "ECONOMIQUE" ? "warning" : "danger"} className="align-self-start">
                                                        {vehicle.reform || "-"}
                                                    </Badge>
                                                </div>

                                                <div className="d-flex flex-wrap gap-1 mb-2">
                                                    {vehicle.carburant && <Badge color="light" className="text-dark">{vehicle.carburant}</Badge>}
                                                    {vehicle.puissFiscale && <Badge color="light" className="text-dark">{vehicle.puissFiscale} CV</Badge>}
                                                    {vehicle.nbrCylindres && <Badge color="light" className="text-dark">{vehicle.nbrCylindres} cyl.</Badge>}
                                                </div>

                                                <Row className="g-2 mb-2 vehicle-card-details">
                                                    <Col xs={6}>
                                                        <div className="text-muted small">Immatriculation</div>
                                                        <div className="fw-semibold">{vehicle.immatriculation || "-"}</div>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <div className="text-muted small">Prix achat</div>
                                                        <div className="fw-semibold">{formatAmount(vehicle.prixAchat)} MAD</div>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <div className="text-muted small">Mise en circulation</div>
                                                        <div className="fw-semibold">{vehicle.firstMec || "-"}</div>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <div className="text-muted small">Proprietaire</div>
                                                        <div className="fw-semibold text-truncate">{vehicle.proprietere || "-"}</div>
                                                    </Col>
                                                </Row>

                                                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                    <Link to={DATABASE_VEHICULE_EDIT.replace(":id", vehicle.id)} className="btn btn-sm btn-light">
                                                        <i className="mdi mdi-pencil me-1"></i>
                                                        Modifier
                                                    </Link>
                                                    <Button size="sm" color="light" className="text-danger" onClick={() => handleDelete(vehicle)}>
                                                        <i className="mdi mdi-trash-can me-1"></i>
                                                        Supprimer
                                                    </Button>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </div>
                            <div className="mt-3">
                                {renderPagination()}
                            </div>
                        </>
                    )}
                </Container>
            </div>

            <Modal isOpen={record.open} toggle={() => setRecord({ ...record, open: false })} backdrop="static">
                <ModalHeader toggle={() => setRecord({ ...record, open: false })}>
                    {t("text.confirmation")}
                </ModalHeader>
                <ModalBody>
                    <p>{t("text.msgDelete")}</p>
                    <ModalFooter>
                        <Button type="button" color="light" onClick={() => setRecord({ ...record, open: false })}>{t("actions.close")}</Button>
                        <Button type="button" color="primary" onClick={confirmDelete}>{t("actions.confirm")}</Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            {photoViewer.open && photoViewer.photos.length > 0 && (
                <Lightbox
                    mainSrc={resolveDocumentUrl(photoViewer.photos[photoViewer.index])}
                    nextSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length])}
                    prevSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length])}
                    imageTitle={photoViewer.title}
                    imageCaption={photoViewer.photos[photoViewer.index].filenameUser || photoViewer.photos[photoViewer.index].filename}
                    onCloseRequest={closePhotoViewer}
                    onMovePrevRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + prev.photos.length - 1) % prev.photos.length,
                    }))}
                    onMoveNextRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + 1) % prev.photos.length,
                    }))}
                    enableZoom={true}
                />
            )}
        </React.Fragment>
    );
}

export default VehiculePage;
